import React, { useEffect, ReactNode } from "react";
import Button from "../shared/Button/Button";

interface ClientSecurityMessageModalProps {
  isOpen: boolean;
  title?: string;
  content?: ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  modalBg?: string;
  crossColor?: string;
  textBg?: string;
  hideCloseIcon?: boolean;
  showCloseIconInsideContent?: boolean;
  /** Popup banner only: close control sits above the image. */
  closeOutside?: boolean;
  isContentWidth?: boolean;
}

const ClientSecurityMessageModal: React.FC<ClientSecurityMessageModalProps> = ({
  isOpen,
  title,
  content,
  onClose,
  children,
  textBg = "black",
  modalBg = "bg-white",
  crossColor = "text-black",
  hideCloseIcon,
  showCloseIconInsideContent,
  closeOutside,
  isContentWidth,
}) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    } else {
      document.removeEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`m-auto fixed overflow-y-scroll z-[100] inset-0 bg-black/50 flex items-center justify-center`}
      onClick={onClose}
    >
      <div
        className={`${modalBg} rounded-lg ${
          isContentWidth ? "" : "w-[90%]"
        } max-w-[90%] mx-auto relative scroll ${
          closeOutside ? "overflow-visible" : "max-h-[90vh] overflow-y-auto"
        } ${
          title || !hideCloseIcon ? "px-4 py-3" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || !hideCloseIcon) && (
          <div className="flex justify-between items-center pb-3">
            {title && (
              <h2 className={`text-${textBg} text-xl font-semibold`}>
                {title}
              </h2>
            )}
            {!hideCloseIcon && (
              <Button onClick={onClose} className="text-lg">
                <svg
                  className="w-5 h-5"
                  fill="black"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </Button>
            )}
          </div>
        )}

        {closeOutside && (
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute right-0 top-0 z-20 flex h-[26px] w-[26px] translate-x-[40%] -translate-y-[40%] items-center justify-center rounded-full border border-white bg-black text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)]"
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        )}
        <div
          className={`relative h-fit ${
            closeOutside
              ? "max-h-[calc(100vh-7.5rem)] overflow-y-auto"
              : "overflow-y-auto"
          }`}
          onClick={
            hideCloseIcon && !showCloseIconInsideContent ? onClose : null
          }
        >
          {children}

          {showCloseIconInsideContent && !closeOutside && (
            <div className="absolute top-4 right-4 z-10">
              <Button
                onClick={onClose}
                className="text-lg text-white cursor-pointer hover:bg-gray-100/30 rounded-md"
              >
                <svg
                  className="w-6 h-6"
                  fill="black"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientSecurityMessageModal;
